import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, Clock, ArrowLeft, Tag as TagIcon, ArrowRight } from 'lucide-react';
import DOMPurify from 'dompurify';
import { getBlogPost, getBlogPosts } from '@/lib/api';

const formatDate = (d) => {
  if (!d) return '';
  try {
    return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
  } catch {
    return '';
  }
};

const readingTime = (html) => {
  if (!html) return '1 min read';
  const text = html.replace(/<[^>]+>/g, ' ');
  const words = text.trim().split(/\s+/).filter(Boolean).length;
  return `${Math.max(1, Math.round(words / 220))} min read`;
};

const setMeta = (name, content, attr = 'name') => {
  if (!content) return;
  let tag = document.querySelector(`meta[${attr}="${name}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attr, name);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
};

const BlogPostPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setNotFound(false);
      try {
        const res = await getBlogPost(slug);
        if (cancelled) return;
        setPost(res.data);
        // Related posts (same category, exclude this slug)
        try {
          const params = {};
          if (res.data.category) params.category = res.data.category;
          const rel = await getBlogPosts(params);
          if (!cancelled) {
            setRelated((rel.data.posts || []).filter((p) => p.slug !== slug).slice(0, 3));
          }
        } catch (relErr) {
          if (process.env.NODE_ENV !== 'production') console.error('Related posts fetch failed:', relErr);
        }
      } catch (err) {
        if (!cancelled) setNotFound(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    window.scrollTo(0, 0);
    return () => { cancelled = true; };
  }, [slug]);

  useEffect(() => {
    if (post) {
      document.title = `${post.meta_title || post.title} — Lalita Properties`;
      setMeta('description', post.meta_description || post.excerpt);
      setMeta('og:title', post.title, 'property');
      setMeta('og:description', post.meta_description || post.excerpt, 'property');
      if (post.cover_image) setMeta('og:image', post.cover_image, 'property');
      setMeta('og:type', 'article', 'property');
    }
    return () => {
      document.title = 'Lalita Properties';
    };
  }, [post]);

  if (loading) {
    return (
      <div className="pt-32 min-h-screen bg-white">
        <div className="max-w-3xl mx-auto px-4 animate-pulse">
          <div className="h-8 bg-warm-sand rounded w-1/2 mb-4" />
          <div className="h-12 bg-warm-sand rounded w-3/4 mb-8" />
          <div className="h-64 bg-warm-sand rounded-2xl mb-8" />
          <div className="space-y-3">
            <div className="h-4 bg-warm-sand rounded" />
            <div className="h-4 bg-warm-sand rounded w-5/6" />
            <div className="h-4 bg-warm-sand rounded w-4/6" />
          </div>
        </div>
      </div>
    );
  }

  if (notFound || !post) {
    return (
      <div className="pt-32 min-h-screen bg-white">
        <div className="max-w-2xl mx-auto px-4 text-center py-20">
          <h1 className="font-heading text-3xl font-semibold text-charcoal mb-4">Post not found</h1>
          <p className="text-charcoal-light mb-6">It may have been moved or unpublished.</p>
          <Link to="/blog" className="inline-flex items-center gap-2 text-ubud-jungle font-medium hover:underline">
            <ArrowLeft className="w-4 h-4" /> Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-24 min-h-screen bg-white" data-testid="blog-post-page">
      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button
          onClick={() => navigate('/blog')}
          className="inline-flex items-center gap-2 text-sm text-charcoal-light hover:text-charcoal mb-8"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Blog
        </button>

        {post.category && (
          <span className="inline-block text-xs font-medium tracking-wide uppercase text-ubud-jungle mb-3">
            {post.category}
          </span>
        )}

        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-charcoal leading-tight mb-6"
        >
          {post.title}
        </motion.h1>

        <div className="flex flex-wrap items-center gap-4 text-sm text-charcoal-muted mb-8">
          <span className="inline-flex items-center gap-1.5">
            <Calendar className="w-4 h-4" />{formatDate(post.published_at || post.created_at)}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Clock className="w-4 h-4" />{readingTime(post.content)}
          </span>
          <span>by {post.author || 'Lalita Team'}</span>
        </div>

        {post.cover_image && (
          <motion.img
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            src={post.cover_image}
            alt={post.title}
            className="w-full aspect-[16/9] object-cover rounded-3xl mb-10"
          />
        )}

        {post.excerpt && (
          <p className="font-heading text-xl text-charcoal-light italic leading-relaxed mb-8 border-l-4 border-ubud-jungle pl-4">
            {post.excerpt}
          </p>
        )}

        <div
          className="prose prose-lg max-w-none prose-headings:font-heading prose-headings:text-charcoal prose-p:text-charcoal prose-p:font-body prose-a:text-ubud-jungle prose-a:no-underline hover:prose-a:underline prose-img:rounded-xl prose-blockquote:border-ubud-jungle prose-blockquote:text-charcoal-light"
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(post.content || '', {
              ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 's', 'code', 'pre', 'blockquote', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'a', 'img', 'hr', 'span'],
              ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'class', 'target', 'rel'],
              ALLOW_DATA_ATTR: false,
            }),
          }}
        />

        {post.tags && post.tags.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 mt-12 pt-8 border-t border-gray-100">
            <span className="text-sm font-medium text-charcoal-light">Tags:</span>
            {post.tags.map((t) => (
              <Link
                key={t}
                to={`/blog?tag=${encodeURIComponent(t)}`}
                className="px-3 py-1 rounded-full text-xs font-medium bg-warm-sand text-charcoal hover:bg-warm-sand-dark inline-flex items-center gap-1"
              >
                <TagIcon className="w-3 h-3" />{t}
              </Link>
            ))}
          </div>
        )}

        {/* CTA */}
        <div className="mt-16 bg-warm-sand rounded-3xl p-8 sm:p-12 text-center">
          <h3 className="font-heading text-2xl sm:text-3xl font-semibold text-charcoal mb-3">
            Thinking about your own Bali villa?
          </h3>
          <p className="font-body text-charcoal-light mb-6">
            Whether you're buying, renting or looking for full property management — we'd love to chat.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-ubud-jungle text-white rounded-full px-6 py-3 font-medium hover:bg-ubud-jungle-dark transition-colors"
          >
            Get in touch <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </article>

      {/* Related */}
      {related.length > 0 && (
        <section className="bg-warm-sand py-16 mt-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-heading text-2xl sm:text-3xl font-semibold text-charcoal mb-8">
              You might also like
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {related.map((r) => (
                <Link key={r.id} to={`/blog/${r.slug}`} className="group block">
                  <div className="aspect-[4/3] overflow-hidden rounded-2xl mb-4 bg-white">
                    {r.cover_image ? (
                      <img src={r.cover_image} alt={r.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    ) : (
                      <div className="w-full h-full bg-ubud-jungle/10 flex items-center justify-center">
                        <span className="font-heading text-2xl text-ubud-jungle/40">Lalita</span>
                      </div>
                    )}
                  </div>
                  <h4 className="font-heading text-lg font-semibold text-charcoal group-hover:text-ubud-jungle transition-colors line-clamp-2">
                    {r.title}
                  </h4>
                  <p className="text-xs text-charcoal-muted mt-2">{formatDate(r.published_at || r.created_at)}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default BlogPostPage;
